import { useEffect, useRef } from "react";
import { useRoom } from "../components/context/room/room";
import { useOrganization } from "../components/context/organization/organization";
import useLayout from "./layout";
import useUser from "./user";
import {
  initialUserState,
  LayoutState,
  LocalStorageKeys,
  UserState,
} from "../types";

export const useDefaultSettings = () => {
  const { room, organization: roomOrg } = useRoom();
  const { organization: userOrg } = useOrganization();
  const [, setLayout] = useLayout();
  const [, setUser] = useUser();

  const initializedOrgDefaults = useRef(false);
  useEffect(() => {
    if (initializedOrgDefaults.current || (!roomOrg && !userOrg)) {
      return;
    }
    initializedOrgDefaults.current = true;

    let storedSettings: Partial<LayoutState["settings"]> = {};
    try {
      storedSettings = JSON.parse(
        localStorage.getItem(LocalStorageKeys.LAYOUT_SETTINGS) || ""
      ) as Partial<LayoutState["settings"]>;
    } catch {
      // Ignore parse errors
    }

    setLayout((prevState) => ({
      ...prevState,
      settings: {
        ...prevState.settings,
        textSize:
          storedSettings.textSize ??
          roomOrg?.default_text_translation_setting.text_size ??
          userOrg?.default_text_translation_setting.text_size ??
          prevState.settings.textSize,
        textColor:
          storedSettings.textColor ??
          roomOrg?.default_text_translation_setting.text_color ??
          userOrg?.default_text_translation_setting.text_color ??
          prevState.settings.textColor,
        textBackgroundColor:
          storedSettings.textBackgroundColor ??
          roomOrg?.default_text_translation_setting.text_background_color ??
          userOrg?.default_text_translation_setting.text_background_color ??
          prevState.settings.textBackgroundColor,
        textSpeed:
          storedSettings.textSpeed ??
          roomOrg?.default_text_translation_setting.text_speed ??
          userOrg?.default_text_translation_setting.text_speed ??
          prevState.settings.textSpeed,
      },
    }));
  }, [roomOrg, userOrg, setLayout, setUser]);

  const initializedRoomDefaults = useRef(false);
  useEffect(() => {
    if (initializedRoomDefaults.current || !room) {
      return;
    }
    initializedRoomDefaults.current = true;

    const storedVoiceId = localStorage.getItem(LocalStorageKeys.VOICE_AVATAR);

    const storedAiVoiceGender = storedVoiceId
      ? storedVoiceId.split(" ")[0]
      : null;
    const storedAiVoice = storedVoiceId ? storedVoiceId.split(" ")[1] : null;

    const [aiVoiceGender, aiVoice] = room.default_voice_avatar.split(" ");
    setUser((prevState) => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        aiVoiceGender: (storedAiVoiceGender ||
          aiVoiceGender ||
          initialUserState.metadata
            .aiVoiceGender) as UserState["metadata"]["aiVoiceGender"],
        aiVoice: (storedAiVoice ||
          aiVoice ||
          initialUserState.metadata
            .aiVoice) as UserState["metadata"]["aiVoice"],
      },
    }));
  }, [room, setUser]);
};
